/**
 * Bootstrap do Sentry no frontend.
 *
 * Mesma política de PII do backend (ver apps/api/src/infra/sentry/).
 * Se VITE_SENTRY_DSN não estiver setado, init vira no-op.
 *
 * O ErrorBoundary é exportado daqui também — o `main.tsx` envolve o
 * <App/> com ele, garantindo que crashes de render virem evento Sentry
 * em vez de tela branca.
 */
import type { ComponentType, ReactNode } from 'react';

type SentryModule = typeof import('@sentry/react');
let sentryModule: SentryModule | null = null;

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  // Import dinâmico mantém bundle limpo em dev sem dep instalada.
   
  // @ts-ignore - dep opcional em tempo de tipagem
  const Sentry: SentryModule = await import('@sentry/react');
  sentryModule = Sentry;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_GIT_SHA as string | undefined) ?? undefined,
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      // Remove PII conhecida do request body se cair em integration de fetch.
      if (event.request?.data && typeof event.request.data === 'object') {
        const body = event.request.data as Record<string, unknown>;
        for (const k of [
          'password',
          'passwordHash',
          'currentPassword',
          'newPassword',
          'otp',
          'cpf',
        ]) {
          if (k in body) body[k] = '[Filtered]';
        }
      }
      return event;
    },
  });
}

/**
 * Lazy ErrorBoundary: usa o Sentry se estiver inicializado, senão
 * renderiza children sem captura. Permite que o app rode em dev sem
 * precisar instalar @sentry/react.
 */
export function getSentryErrorBoundary(): ComponentType<{
  children: ReactNode;
  fallback?: ReactNode;
}> | null {
  if (!sentryModule) return null;
  return sentryModule.ErrorBoundary as ComponentType<{
    children: ReactNode;
    fallback?: ReactNode;
  }>;
}
